import type { ComboboxItem } from '@mantine/core';
import type { TFunction } from 'i18next';
import type { FilterOption } from '../../../../../components/filters/filter-panel.type';
import { resolvePhaseLabels } from '../../../../../helpers/phase-labels/phase-labels';
import type { PhaseLabel } from '../../../../../helpers/phase-labels/phase-labels.type';
import type { StatusLabelResolver } from '../../../../../helpers/status-labels/status-labels.type';
import { capitalizeFirstLetter } from '../../../../../helpers/string-helpers/string-helpers';
import { extractPhaseKeys } from '../../../../../helpers/subgroup-helpers/subgroup-helpers';
import { useFlowByPhaseTabStore } from '../../../../../store/strategy-store/strategy-store';
import { secondaryBase } from '../../../../../styles/design-tokens';
import { Icon } from '../../../../../ui-library/icon/icon';
import type { ExtendedComboboxItem } from '../../../../../ui-library/select/select.type';
import { StrategyTab } from '../../../strategy.type';
import { Phase } from '../../flow-by-phase-tab/flow-by-phase-tab.type';
import { RelatedTasksMode } from '../related-tasks-tab.type';

/**
 * Returns an array of filter options for task types
 * Each option contains the task type as value and a capitalized label
 *
 * @param {string[]} taskTypes - Array of task type strings to convert to filter options
 * @returns {FilterOption[]} Array of filter options with task type values and capitalized labels
 */
const getTaskTypeOptions = (taskTypes: string[]): FilterOption[] => {
  return taskTypes.map((type) => ({
    value: type,
    label: capitalizeFirstLetter(type),
  }));
};

/**
 * Returns an array of all available phase options (groups or subgroups based on useGroups)
 *
 * Sub-group option `value`s stay the raw phase key because they are written to
 * the related-tasks store and sent back to the API; only the label is cased.
 *
 * @param {boolean} useGroups - Whether to return group phases or subgroups
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @param {PhaseLabel[]} [phases] - Per-workflow bucket-label overrides for group phases
 * @param {StatusLabelResolver} [resolveStatusLabelForDisplay] - Maps a raw sub-group key to
 *   its source-cased status label; omit to display raw keys
 * @returns {ComboboxItem[]} Array of all phase options
 *
 * @example
 * // Sub-group mode: labels are cased for display, values stay raw for the store
 * getPhaseOptions(false, t, undefined, getStatusLabel);
 * // [{ value: 'all', label: 'All Statuses' }, { value: 'in progress', label: 'In Progress' }]
 */
const getPhaseOptions = (
  useGroups: boolean,
  t: TFunction,
  phases?: PhaseLabel[],
  resolveStatusLabelForDisplay?: StatusLabelResolver,
): ComboboxItem[] => {
  // Return group phase options
  if (useGroups) {
    // Honor per-workflow `bucket_label_overrides` when the caller
    // provides `phases` (read reactively from the Flow-by-Phase store).
    // Falls back to i18n defaults inside `resolvePhaseLabels`.
    const labels = resolvePhaseLabels(phases, t);
    return [
      {
        value: 'all',
        label: t('strategy.filters.all_statuses'),
      },
      { value: Phase.Blocked, label: labels[Phase.Blocked] },
      { value: Phase.Backlog, label: labels[Phase.Backlog] },
      { value: Phase.Wip, label: labels[Phase.Wip] },
      { value: Phase.Review, label: labels[Phase.Review] },
      { value: Phase.Test, label: labels[Phase.Test] },
      { value: Phase.Deployable, label: labels[Phase.Deployable] },
      { value: Phase.Done, label: labels[Phase.Done] },
    ];
  }

  // Return subgroup options from Flow by Phase data
  const flowByPhaseData = useFlowByPhaseTabStore.getState().data;

  if (!flowByPhaseData) {
    return [
      {
        value: 'all',
        label: t('strategy.filters.all_statuses'),
      },
    ];
  }

  const subgroupKeys = extractPhaseKeys(flowByPhaseData);

  return [
    {
      value: 'all',
      label: t('strategy.filters.all_statuses'),
    },
    ...subgroupKeys.map((key) => ({
      value: key,
      label: resolveStatusLabelForDisplay ? resolveStatusLabelForDisplay(key) : key,
    })),
  ];
};

/**
 * Returns an array of options for the related tasks mode dropdown
 * Each option contains a label for display and a value from the RelatedTasksMode enum
 *
 * @param {StrategyTab} currentStrategyTab - The currently active strategy tab
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {ExtendedComboboxItem[]} Array of options for the related tasks mode selection
 */
const getRelatedTasksModeOptions = (currentStrategyTab: StrategyTab, t: TFunction): ExtendedComboboxItem[] => {
  const options = [
    {
      label: t('strategy.initiative_performance.labels.contributing_tasks', {
        work_units: t('common.work_units'),
      }),
      value: RelatedTasksMode.ContributingTasks,
      leftSection: <Icon name="task_alt" size={20} color={secondaryBase} />,
    },
    {
      label: t('strategy.initiative_performance.labels.contributing_epics'),
      value: RelatedTasksMode.ContributingEpics,
      leftSection: <Icon name="move_down" size={20} color={secondaryBase} />,
    },
    {
      label: t('strategy.initiative_performance.labels.contributing_teams'),
      value: RelatedTasksMode.ContributingTeams,
      leftSection: <Icon name="people_alt" size={20} color={secondaryBase} />,
    },
    {
      label: t('strategy.initiative_performance.labels.contributing_initiatives'),
      value: RelatedTasksMode.ContributingInitiatives,
      leftSection: <Icon name="move_down" size={20} color={secondaryBase} />,
    },
    {
      label: t('strategy.initiative_performance.labels.contributing_outcomes'),
      value: RelatedTasksMode.ContributingOutcomes,
      leftSection: <Icon name="ads_click" size={20} color={secondaryBase} />,
    },
  ];

  switch (currentStrategyTab) {
    case StrategyTab.Initiatives:
      return options.slice(0, 3);
    case StrategyTab.Outcomes:
      return options.slice(0, 4);
    case StrategyTab.Goals:
      return options.slice(0, 5);
    case StrategyTab.Epics:
      return [options[0], options[2]]; // Contributing Tasks and Contributing Teams
    default:
      return options;
  }
};

export { getPhaseOptions, getRelatedTasksModeOptions, getTaskTypeOptions };
